import type { NextApiRequest } from 'next'
import type winston from 'winston'

import type { ErrorLogMetadata } from './logger.types'

const isServer = typeof window === 'undefined'
const isDevelopment = process.env.NODE_ENV === 'development'

// Helper to determine if request is from localhost
const isLocalhost = (req?: NextApiRequest): boolean => {
    if (!req) return false
    const host = req.headers.host || ''
    return (
        host.startsWith('localhost') ||
        host.startsWith('local.step') ||
        host.startsWith('127.0.0.1')
    )
}

// Mock logger for browser
const createMockLogger = () => ({
    error: console.error,
    warn: console.warn,
    info: console.info,
    debug: console.debug,
    // eslint-disable-next-line no-console
    log: console.log,
    child: () => createMockLogger(),
})

// Create Winston logger with Cloud Logging integration
const createLogger = () => {
    if (!isServer) {
        return createMockLogger()
    }

    const winston = require('winston')

    const transports: winston.transport[] = [
        // Console transport outputs to stdout/stderr
        new winston.transports.Console({
            format: winston.format.combine(
                winston.format.colorize(),
                // Only show timestamps in production
                ...(isDevelopment ? [] : [winston.format.timestamp()]),
                winston.format.printf(
                    ({ timestamp, level, message, ...meta }: any) => {
                        const metaStr = Object.keys(meta).length
                            ? JSON.stringify(meta)
                            : ''
                        // Show timestamp in production, omit in development
                        const timestampPrefix = timestamp ? `${timestamp} ` : ''
                        return `${timestampPrefix}[${level}]: ${message} ${metaStr}`
                    },
                ),
            ),
        }),
    ]

    // Add Cloud Logging transport when GCP_PROJECT_ID is configured, on server-side, and at runtime (not during build)
    const isBuild = process.env.NEXT_PHASE === 'phase-production-build'
    if (process.env.GCP_PROJECT_ID && isServer && !isBuild) {
        try {
            // Dynamically import server-only package to avoid bundling in client
            const { LoggingWinston } = require('@google-cloud/logging-winston')
            const loggingWinston = new LoggingWinston({
                projectId: process.env.GCP_PROJECT_ID,
                // Uses Application Default Credentials (ADC) automatically
                // No keyFilename needed when running on Google Cloud infrastructure
                logName: 'web-app-logs',
            })
            transports.push(loggingWinston)
        } catch (error) {
            console.error('Failed to initialize Cloud Logging:', error)
        }
    }

    return winston.createLogger({
        level: process.env.LOG_LEVEL || 'info',
        format: winston.format.combine(
            winston.format.timestamp(),
            winston.format.errors({ stack: true }),
            winston.format.json(),
        ),
        transports,
    })
}

export const logger = createLogger()

// Rebind console methods to use winston logger on server-side
if (isServer) {
    const originalConsole = {
        log: console.log, // eslint-disable-line no-console
        info: console.info,
        warn: console.warn,
        error: console.error,
        debug: console.debug,
    }

    // eslint-disable-next-line no-console
    console.log = (...args: unknown[]) =>
        logger.info(String(args[0]), args.slice(1))

    console.info = (...args: unknown[]) =>
        logger.info(String(args[0]), args.slice(1))

    console.warn = (...args: unknown[]) =>
        logger.warn(String(args[0]), args.slice(1))

    console.error = (...args: unknown[]) =>
        logger.error(String(args[0]), args.slice(1))

    console.debug = (...args: unknown[]) =>
        logger.debug(String(args[0]), args.slice(1))

    // Store original console for potential restore
    ;(logger as any).originalConsole = originalConsole
}

// Helper to create a child logger with request metadata
// Excludes metadata when request is from localhost
export const withRequest = (req: NextApiRequest) => {
    const shouldIncludeMetadata = !isLocalhost(req)

    const requestMetadata = shouldIncludeMetadata
        ? {
              httpRequest: {
                  requestMethod: req.method,
                  requestUrl: req.url,
                  userAgent: req.headers['user-agent'],
              },
          }
        : {}

    return logger.child(requestMetadata)
}

// Attach withRequest as a method on logger for convenience
Object.assign(logger, { withRequest })

// Helper for structured error logging
export const logError = (
    message: string,
    error?: Error,
    metadata?: ErrorLogMetadata,
) => {
    logger.error(message, {
        error: error?.message,
        stack: error?.stack,
        ...metadata,
    })
}
